import { HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, throwError } from 'rxjs';

import { BaseApiService } from '@core/api/base-api.service';
import { API_ENDPOINTS, ApiEndpointDefinition } from '@core/constants/api-endpoints';
import { SKIP_AUTH } from '@core/interceptors/auth-token.interceptor';
import { SKIP_ERROR_NOTIFICATION } from '@core/interceptors/error.interceptor';
import { ApiError } from '@core/models/api-error.model';
import { ApiResponse } from '@core/models/api-response.model';
import { environment } from '@env/environment';

import { AuthSession } from '../../models/auth-session.model';
import { AuthMapper } from '../../models/auth.mapper';
import {
  FastLoginChallengeDto,
  FastLoginRequestBody,
  FastLoginResendBody,
  FastLoginSessionDto,
  FastLoginVerifyBody,
} from '../../models/fast-login.dto';
import { AuthResultDto, LoginRequest, SocialLoginRequest } from '../../models/login.dto';
import {
  RequestPasswordResetRequest,
  RequestPasswordResetResponseDto,
  ResetPasswordRequest,
  VerifyOtpRequest,
  VerifyOtpResponseDto,
} from '../../models/password-reset.dto';
import {
  RegisterAccountRequest,
  RegisterAccountResponseDto,
  RegisterCompleteRequest,
  RegisterCompleteResponseDto,
} from '../../models/register.dto';
import { AuthRepositoryContract } from './auth.repository.contract';

@Injectable({ providedIn: 'root' })
export class AuthRepository implements AuthRepositoryContract {
  private readonly api = inject(BaseApiService);

  /**
   * Every call here happens before a session exists, so none should carry an
   * Authorization header. Each page renders failures inline under its form,
   * so the error interceptor's toast is switched off too — otherwise the
   * same failure would be reported twice.
   */
  private static readonly anonymous = {
    context: new HttpContext().set(SKIP_AUTH, true).set(SKIP_ERROR_NOTIFICATION, true),
  };

  login(body: LoginRequest): Observable<AuthSession> {
    const endpoint = API_ENDPOINTS.auth.login;
    return (
      this.notConnected('Sign-in', endpoint) ??
      this.api
        .post<LoginRequest, ApiResponse<AuthResultDto>>(
          endpoint.path,
          body,
          undefined,
          AuthRepository.anonymous,
        )
        .pipe(map((response) => AuthMapper.fromDto(AuthRepository.unwrap(response))))
    );
  }

  socialLogin(body: SocialLoginRequest): Observable<AuthSession> {
    const endpoint = API_ENDPOINTS.auth.social;
    return (
      this.notConnected('Social sign-in', endpoint) ??
      this.api
        .post<SocialLoginRequest, ApiResponse<AuthResultDto>>(
          endpoint.path,
          body,
          undefined,
          AuthRepository.anonymous,
        )
        .pipe(map((response) => AuthMapper.fromDto(AuthRepository.unwrap(response))))
    );
  }

  registerAccount(body: RegisterAccountRequest): Observable<string> {
    const endpoint = API_ENDPOINTS.auth.registerAccount;
    return (
      this.notConnected('Registration', endpoint) ??
      this.api
        .post<RegisterAccountRequest, ApiResponse<RegisterAccountResponseDto>>(
          endpoint.path,
          body,
          undefined,
          AuthRepository.anonymous,
        )
        .pipe(map((response) => AuthRepository.unwrap(response).registrationId))
    );
  }

  registerComplete(body: RegisterCompleteRequest): Observable<AuthSession> {
    const endpoint = API_ENDPOINTS.auth.registerComplete;
    return (
      this.notConnected('Registration', endpoint) ??
      this.api
        .post<RegisterCompleteRequest, ApiResponse<RegisterCompleteResponseDto>>(
          endpoint.path,
          body,
          undefined,
          AuthRepository.anonymous,
        )
        .pipe(map((response) => AuthMapper.fromDto(AuthRepository.unwrap(response))))
    );
  }

  logout(): Observable<string | undefined> {
    const endpoint = API_ENDPOINTS.auth.logout;
    return (
      this.notConnected('Sign-out', endpoint) ??
      this.api
        // Authenticated (the bearer identifies the session being ended), and
        // quiet: a failure here must not toast — the local sign-out proceeds
        // regardless, see LogoutUseCase.
        .post<null, ApiResponse<{ message?: string } | null>>(endpoint.path, null, undefined, {
          context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true),
        })
        .pipe(map((response) => AuthRepository.unwrap(response)?.message || undefined))
    );
  }

  requestPasswordReset(body: RequestPasswordResetRequest): Observable<string | undefined> {
    return this.emailCode(API_ENDPOINTS.auth.forgotPassword, body);
  }

  resendOtp(body: RequestPasswordResetRequest): Observable<string | undefined> {
    return this.emailCode(API_ENDPOINTS.auth.resendOtp, body);
  }

  verifyOtp(body: VerifyOtpRequest): Observable<VerifyOtpResponseDto> {
    const endpoint = API_ENDPOINTS.auth.verifyOtp;
    return (
      this.notConnected('Code verification', endpoint) ??
      this.api
        .post<VerifyOtpRequest, ApiResponse<VerifyOtpResponseDto>>(
          endpoint.path,
          body,
          undefined,
          AuthRepository.anonymous,
        )
        .pipe(map((response) => AuthRepository.unwrap(response)))
    );
  }

  resetPassword(body: ResetPasswordRequest): Observable<void> {
    const endpoint = API_ENDPOINTS.auth.resetPassword;
    return (
      this.notConnected('Password reset', endpoint) ??
      this.api
        .post<ResetPasswordRequest, ApiResponse<unknown>>(
          endpoint.path,
          body,
          undefined,
          AuthRepository.anonymous,
        )
        .pipe(map((response) => void AuthRepository.unwrap(response)))
    );
  }

  fastLoginRequest(body: FastLoginRequestBody): Observable<FastLoginChallengeDto> {
    const endpoint = API_ENDPOINTS.auth.fastLoginRequest;
    return (
      this.notConnected('Fast login', endpoint) ??
      this.api
        .post<FastLoginRequestBody, ApiResponse<FastLoginChallengeDto>>(
          endpoint.path,
          body,
          undefined,
          AuthRepository.anonymous,
        )
        .pipe(map((response) => AuthRepository.unwrap(response)))
    );
  }

  fastLoginResend(body: FastLoginResendBody): Observable<FastLoginChallengeDto> {
    const endpoint = API_ENDPOINTS.auth.fastLoginResend;
    return (
      this.notConnected('Fast login', endpoint) ??
      this.api
        .post<FastLoginResendBody, ApiResponse<FastLoginChallengeDto>>(
          endpoint.path,
          body,
          undefined,
          AuthRepository.anonymous,
        )
        .pipe(map((response) => AuthRepository.unwrap(response)))
    );
  }

  fastLoginVerify(body: FastLoginVerifyBody): Observable<AuthSession> {
    const endpoint = API_ENDPOINTS.auth.fastLoginVerify;
    return (
      this.notConnected('Fast login', endpoint) ??
      this.api
        .post<FastLoginVerifyBody, ApiResponse<FastLoginSessionDto>>(
          endpoint.path,
          body,
          undefined,
          AuthRepository.anonymous,
        )
        .pipe(map((response) => AuthMapper.fromFastLoginDto(AuthRepository.unwrap(response))))
    );
  }

  /** `/forgot-password` and `/resend` share a shape: email in, a line of text out. */
  private emailCode(
    endpoint: ApiEndpointDefinition,
    body: RequestPasswordResetRequest,
  ): Observable<string | undefined> {
    return (
      this.notConnected('Password reset', endpoint) ??
      this.api
        .post<RequestPasswordResetRequest, ApiResponse<RequestPasswordResetResponseDto | null>>(
          endpoint.path,
          body,
          undefined,
          AuthRepository.anonymous,
        )
        .pipe(map((response) => AuthRepository.unwrap(response)?.message || undefined))
    );
  }

  /**
   * Guard rail for routes the backend has not confirmed: rather than posting
   * a guessed path and 404-ing in front of a visitor, fail with a message
   * that says what is missing. Returns null for a confirmed route with a
   * configured base URL, so the caller falls through to the real call.
   */
  private notConnected<T>(feature: string, endpoint: ApiEndpointDefinition): Observable<T> | null {
    if (environment.apiUrl && endpoint.confirmed) {
      return null;
    }
    const missing = environment.apiUrl
      ? `confirm the ${endpoint.method} ${endpoint.path} contract`
      : 'set environment.apiUrl';
    return throwError(() => new Error(`${feature} is not connected yet: ${missing}.`));
  }

  /**
   * The gateway can report a failure inside a 2xx (`success: false`) as well
   * as through an HTTP status. Turn the former into an ApiError so both paths
   * reach the same `error` branch, carrying the server's message and its
   * per-field errors.
   */
  private static unwrap<T>(response: ApiResponse<T>): T {
    if (response && response.success === false) {
      throw ApiError.fromResponse(response);
    }
    return response?.data;
  }
}
